import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  User, 
  Bell, 
  Shield, 
  Settings, 
  HelpCircle, 
  X, 
  Lock, 
  LogOut, 
  Download, 
  Trash2,
  FileText,
  Mail,
  ExternalLink
} from 'lucide-react';
import { useUserProfile } from '@/hooks/useUserProfile';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { DeleteAccountDialog } from './DeleteAccountDialog';

interface SettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const SettingsModal = ({ open, onOpenChange }: SettingsModalProps) => {
  const { profile, refetch } = useUserProfile();
  const { user: authUser, signOut } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Personal Info State
  const [fullName, setFullName] = useState('');
  const [bio, setBio] = useState('');
  const [location, setLocation] = useState('');
  const [phone, setPhone] = useState('');
  const [skillLevel, setSkillLevel] = useState('Intermedio');
  const [sports, setSports] = useState<string[]>([]);

  // Notification Settings
  const [emailNotifications, setEmailNotifications] = useState(true);
  const [eventReminders, setEventReminders] = useState(true);
  const [friendRequests, setFriendRequests] = useState(true);

  const sportsList = ['Tenis', 'Pádel', 'Pickleball'];

  // Initialize state from profile
  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name || '');
      setBio(profile.bio || '');
      setLocation(profile.location || '');
      setPhone(profile.phone || '');
      setSkillLevel(profile.skill_level || 'Intermedio');
      setSports(profile.preferred_sports || []);
    }
  }, [profile]);

  const toggleSport = (sport: string) => {
    setSports(prev => 
      prev.includes(sport) 
        ? prev.filter(s => s !== sport)
        : [...prev, sport]
    );
  };

  const handleSavePersonalInfo = async () => {
    if (!profile) return;
    
    setLoading(true);
    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: fullName,
          bio,
          location,
          phone,
          skill_level: skillLevel,
          preferred_sports: sports,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', profile.user_id);

      if (error) throw error;

      toast({
        title: 'Perfil actualizado',
        description: 'Tu información personal se ha guardado correctamente.',
      });
      
      refetch();
    } catch (error) {
      console.error('Error updating profile:', error);
      toast({
        title: 'Error',
        description: 'No se pudo actualizar tu perfil. Inténtalo de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSaveNotifications = () => {
    toast({
      title: 'Preferencias guardadas',
      description: 'Tus preferencias de notificaciones se han actualizado.',
    });
  };

  const handlePasswordReset = async () => {
    if (!authUser?.email) return;

    try {
      const { error } = await supabase.auth.resetPasswordForEmail(authUser.email, {
        redirectTo: `${window.location.origin}/auth`,
      });

      if (error) throw error;

      toast({
        title: 'Email enviado',
        description: 'Revisa tu correo para restablecer tu contraseña.',
      });
    } catch (error) {
      console.error('Error sending password reset:', error);
      toast({
        title: 'Error',
        description: 'No se pudo enviar el email. Inténtalo de nuevo.',
        variant: 'destructive',
      });
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      onOpenChange(false);
      navigate('/');
      toast({
        title: 'Sesión cerrada',
        description: 'Has cerrado sesión correctamente.',
      });
    } catch (error) {
      console.error('Error signing out:', error);
      toast({
        title: 'Error',
        description: 'No se pudo cerrar la sesión.',
        variant: 'destructive',
      });
    }
  };

  const handleDownloadData = async () => {
    if (!authUser?.id) return;

    setDownloading(true);
    try {
      // Get all user data
      const { data: profileData } = await supabase
        .from('profiles')
        .select('*')
        .eq('user_id', authUser.id)
        .single();

      const { data: registrations } = await supabase
        .from('registrations')
        .select('*')
        .eq('user_id', authUser.id);

      const { data: achievements } = await supabase
        .from('achievements')
        .select('*')
        .eq('user_id', authUser.id);

      const userData = {
        profile: profileData,
        registrations: registrations || [],
        achievements: achievements || [],
        exportDate: new Date().toISOString(),
        email: authUser.email
      };

      // Create and download JSON file
      const blob = new Blob([JSON.stringify(userData, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mis-datos-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      toast({ 
        title: 'Datos descargados',
        description: 'Tu archivo de datos se ha descargado correctamente.'
      });
    } catch (error) {
      console.error('Error downloading data:', error);
      toast({ 
        title: 'Error al descargar datos', 
        description: 'No se pudieron descargar tus datos. Inténtalo de nuevo.',
        variant: 'destructive' 
      });
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5" />
              Configuración
            </DialogTitle>
            <DialogDescription>
              Gestiona tu perfil, preferencias y cuenta
            </DialogDescription>
          </DialogHeader>

          <Tabs defaultValue="personal" className="w-full">
            <TabsList className="grid w-full grid-cols-5 h-auto">
              <TabsTrigger value="personal" className="flex flex-col gap-1 py-2 text-xs">
                <User className="h-4 w-4" />
                <span className="hidden sm:inline">Personal</span>
              </TabsTrigger>
              <TabsTrigger value="notifications" className="flex flex-col gap-1 py-2 text-xs">
                <Bell className="h-4 w-4" />
                <span className="hidden sm:inline">Alertas</span>
              </TabsTrigger>
              <TabsTrigger value="privacy" className="flex flex-col gap-1 py-2 text-xs">
                <Shield className="h-4 w-4" />
                <span className="hidden sm:inline">Privacidad</span>
              </TabsTrigger>
              <TabsTrigger value="account" className="flex flex-col gap-1 py-2 text-xs">
                <Lock className="h-4 w-4" />
                <span className="hidden sm:inline">Cuenta</span>
              </TabsTrigger>
              <TabsTrigger value="support" className="flex flex-col gap-1 py-2 text-xs">
                <HelpCircle className="h-4 w-4" />
                <span className="hidden sm:inline">Soporte</span>
              </TabsTrigger>
            </TabsList>

            {/* Personal Info Tab */}
            <TabsContent value="personal" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div>
                  <Label htmlFor="fullName">Nombre Completo</Label>
                  <Input
                    id="fullName"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Tu nombre"
                  />
                </div>

                <div>
                  <Label htmlFor="bio">Biografía</Label>
                  <Textarea
                    id="bio"
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Cuéntanos sobre ti..."
                    rows={3}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="location">Ubicación</Label>
                    <Input
                      id="location"
                      value={location}
                      onChange={(e) => setLocation(e.target.value)}
                      placeholder="Ciudad, País"
                    />
                  </div>

                  <div>
                    <Label htmlFor="phone">Teléfono</Label>
                    <Input
                      id="phone"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value)}
                      placeholder="+34 123 456 789"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="skillLevel">Nivel de Habilidad</Label>
                  <Select value={skillLevel} onValueChange={setSkillLevel}>
                    <SelectTrigger>
                      <SelectValue placeholder="Selecciona tu nivel" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Principiante">Principiante</SelectItem>
                      <SelectItem value="Intermedio">Intermedio</SelectItem>
                      <SelectItem value="Avanzado">Avanzado</SelectItem>
                      <SelectItem value="Profesional">Profesional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Deportes Favoritos</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {sportsList.map(sport => (
                      <Badge
                        key={sport}
                        variant={sports.includes(sport) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => toggleSport(sport)}
                      >
                        {sport}
                        {sports.includes(sport) && (
                          <X className="h-3 w-3 ml-1" />
                        )}
                      </Badge>
                    ))}
                  </div>
                </div>

                <Button onClick={handleSavePersonalInfo} disabled={loading} className="w-full">
                  {loading ? 'Guardando...' : 'Guardar Cambios'}
                </Button>
              </div>
            </TabsContent>

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Notificaciones por Email</Label>
                    <p className="text-sm text-muted-foreground">
                      Recibe actualizaciones por correo electrónico
                    </p>
                  </div>
                  <Switch
                    checked={emailNotifications}
                    onCheckedChange={setEmailNotifications}
                  />
                </div>

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Recordatorios de Eventos</Label>
                    <p className="text-sm text-muted-foreground">
                      Recibe recordatorios antes de tus eventos
                    </p>
                  </div>
                  <Switch
                    checked={eventReminders}
                    onCheckedChange={setEventReminders}
                  />
                </div>

                <Separator />

                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Solicitudes de Amistad</Label>
                    <p className="text-sm text-muted-foreground">
                      Notificaciones de nuevas solicitudes
                    </p>
                  </div>
                  <Switch
                    checked={friendRequests}
                    onCheckedChange={setFriendRequests}
                  />
                </div>

                <Button onClick={handleSaveNotifications} className="w-full">
                  Guardar Preferencias
                </Button>
              </div>
            </TabsContent>

            {/* Privacy Tab */}
            <TabsContent value="privacy" className="space-y-4 mt-4">
              <div className="space-y-4">
                <div className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold mb-2">Visibilidad del Perfil</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Controla quién puede ver tu información
                  </p>
                  <Select defaultValue="public">
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="public">Público - Todos pueden ver</SelectItem>
                      <SelectItem value="friends">Solo Amigos</SelectItem>
                      <SelectItem value="private">Privado - Solo tú</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold mb-2 flex items-center gap-2">
                    <Download className="h-4 w-4" />
                    Tus Datos
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Descarga una copia de toda tu información
                  </p>
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={handleDownloadData}
                    disabled={downloading}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {downloading ? 'Preparando...' : 'Descargar Mis Datos'}
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Account Tab */}
            <TabsContent value="account" className="space-y-4 mt-4">
              <div className="space-y-4">
                {/* Email Display */}
                <div className="p-4 bg-muted rounded-lg">
                  <Label className="text-muted-foreground text-xs uppercase tracking-wide">
                    Email de la cuenta
                  </Label>
                  <p className="font-medium mt-1">{authUser?.email}</p>
                </div>

                {/* Password Reset */}
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handlePasswordReset}
                >
                  <Lock className="h-4 w-4 mr-2" />
                  Cambiar Contraseña
                </Button>

                {/* Sign Out */}
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={handleSignOut}
                >
                  <LogOut className="h-4 w-4 mr-2" />
                  Cerrar Sesión
                </Button>

                <Separator className="my-4" />

                {/* Delete Account */}
                <div className="p-4 border border-destructive/50 rounded-lg bg-destructive/5">
                  <h3 className="font-semibold text-destructive mb-2 flex items-center gap-2">
                    <Trash2 className="h-4 w-4" />
                    Zona de Peligro
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Eliminar tu cuenta es permanente. Todos tus datos serán borrados y no podrás recuperarlos.
                  </p>
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    onClick={() => setDeleteDialogOpen(true)}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Eliminar Cuenta
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Support Tab */}
            <TabsContent value="support" className="space-y-4 mt-4">
              <div className="space-y-3">
                <Button 
                  variant="outline" 
                  className="w-full justify-start"
                  onClick={() => window.open('mailto:soporte@racketplaygrid.com', '_blank')}
                >
                  <Mail className="h-4 w-4 mr-2" />
                  Contactar Soporte
                  <ExternalLink className="h-3 w-3 ml-auto" />
                </Button>

                <div className="pt-4 text-center">
                  <p className="text-xs text-muted-foreground">
                    RacketPlayGrid
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Versión 1.0.0
                  </p>
                </div>
              </div>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      <DeleteAccountDialog 
        open={deleteDialogOpen} 
        onOpenChange={setDeleteDialogOpen} 
      />
    </>
  );
};
